/**
 * Podium d'un live (top 3) : 2e à gauche, 1er au centre (couronne, aura dorée
 * au maximum), 3e à droite ; pastille de rang or / argent / bronze sous
 * l'avatar, puis le nom, le temps de réponse et ce qui a été gagné. Utilisé
 * dans la popup d'une question (`sm`) et sur l'écran de fin (`lg`, avec les
 * marches du podium).
 */

import { Crown } from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';
import { AvatarAura } from '@/features/skillcards/avatar-aura/avatar-aura';
import { formatAnswerTime, podiumDisplayOrder, type PodiumEntry } from '@/lib/live-podium';
import { formatScore } from '@/features/live-ui/ranking-list';

/** Or de la marque : l'aura et la couronne du 1er. */
const PODIUM_GOLD = '#f4c64e';

const RING: Record<number, string> = {
  1: 'ring-[#f4c64e]',
  2: 'ring-[#cfd8e6]',
  3: 'ring-[#d08a4a]',
};
const BADGE: Record<number, string> = {
  1: 'bg-[#f4c64e] text-[#0a2e57]',
  2: 'bg-[#cfd8e6] text-[#0a2e57]',
  3: 'bg-[#d08a4a] text-white',
};
/**
 * Place fixe selon la position dans le classement (pas le rang, qui peut être
 * partagé) : le 1er toujours au centre, même seul ou à deux.
 */
const COLUMN = ['col-start-2', 'col-start-1', 'col-start-3'] as const;
const STEP_HEIGHT: Record<number, string> = { 1: 'h-14', 2: 'h-10', 3: 'h-7' };

export function LivePodium({
  rows,
  unit,
  timeLabel,
  size = 'sm',
  className,
}: {
  rows: PodiumEntry[];
  /** « HORA » ou « pts ». */
  unit: string;
  /** Préfixe du temps (« moy. » sur l'écran de fin), sinon rien. */
  timeLabel?: string;
  /** `lg` : écran de fin (avatars plus grands, marches du podium). */
  size?: 'sm' | 'lg';
  className?: string;
}) {
  const ordered = podiumDisplayOrder(rows);
  if (ordered.length === 0) return null;
  const lg = size === 'lg';
  const byRank = [...ordered].sort((a, b) => a.rank - b.rank);
  return (
    <ol
      aria-label="Podium"
      className={cn('grid grid-cols-3 items-end gap-2 text-center', className)}
      data-testid="live-podium"
    >
      {ordered.map((r) => {
        const first = r.rank === 1;
        const time = formatAnswerTime(r.time_ms);
        const avatar = (
          <AvatarCircle
            name={r.name}
            src={r.avatar_url}
            size={first ? (lg ? 'hero' : 'lg') : lg ? 'lg' : 'md'}
            className={cn('ring-4', RING[r.rank], first && 'shadow-lg')}
          />
        );
        return (
          <li
            key={`${r.rank}-${r.name}`}
            className={cn(
              'row-start-1 flex min-w-0 flex-col items-center',
              COLUMN[byRank.indexOf(r)],
            )}
          >
            <div className="relative flex flex-col items-center">
              {first && (
                <Crown
                  aria-hidden
                  className={cn(
                    'absolute z-10 -translate-y-1/2 drop-shadow',
                    lg ? '-top-1 h-7 w-7' : '-top-0.5 h-5 w-5',
                  )}
                  style={{ color: PODIUM_GOLD, fill: PODIUM_GOLD }}
                />
              )}
              {first ? (
                <AvatarAura
                  power={100}
                  color={PODIUM_GOLD}
                  // L'aura entoure un avatar de la MOITIÉ de son cadre ; les
                  // marges négatives gardent la place de l'avatar seul.
                  className={lg ? '-m-10 h-40 w-40' : '-m-7 h-28 w-28'}
                >
                  {avatar}
                </AvatarAura>
              ) : (
                <div
                  className={cn('flex items-center justify-center', lg ? 'h-20 w-20' : 'h-16 w-16')}
                >
                  {avatar}
                </div>
              )}
              <span
                aria-label={`Rang ${r.rank}`}
                className={cn(
                  'font-display relative z-20 -mt-3 flex h-7 w-7 items-center justify-center rounded-full text-sm font-black shadow ring-2 ring-[#061433]/20',
                  BADGE[r.rank],
                )}
              >
                {r.rank}
              </span>
            </div>
            <span
              className={cn(
                'mt-1 w-full truncate font-bold',
                lg ? 'text-sm' : 'text-xs',
                r.is_me ? 'text-primary-600' : 'text-ink-900',
              )}
            >
              {r.is_me ? 'Toi' : r.name}
            </span>
            {time && (
              <span className="text-ink-500 text-xs tabular-nums">
                {timeLabel ? `${timeLabel} ` : ''}
                {time}
              </span>
            )}
            <span className="text-success-600 text-xs font-black tabular-nums">
              +{formatScore(r.points)} {unit}
            </span>
            {lg && (
              <span
                aria-hidden
                className={cn(
                  'bg-linear-to-b mt-1.5 w-full rounded-t-xl from-[#2f86ff]/45 to-[#2f86ff]/5',
                  STEP_HEIGHT[r.rank],
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
